import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { Observable, Subject } from "rxjs";

import { environment } from "environments/environment";
import {
  KeyValuePairModel,
  ObjectCreatedResponseModel,
  PaginatedModel,
} from "app/shared/models/common.models";
import { FilterParameterService } from "app/shared/services/filter-parameter.service";

import {
  ICreateInboundModel,
  IInboundDeliveryInstructionsResponseModel,
  IInboundDetailsModel,
  IInboundGetFilterModel,
  IInboundListItemModel,
  IInboundNotesUpdateModel,
  IInboundPhoto,
} from "../models/inbound.models";
import { ApiRequestFilters } from "../models/api-request.model";
import { GlobalFilterService } from "./global-filter-state.service";

@Injectable({
  providedIn: "root",
})
export class LogisticsInboundService {
  private searchEvent = new Subject<any>();

  searchEvent$ = this.searchEvent.asObservable();

  private get baseApiUrl(): string {
    return environment.logisticsUrl;
  }

  constructor(
    private http: HttpClient,
    private filterParameterService: FilterParameterService,
    private globalFilterService: GlobalFilterService
  ) {}

  getInboundsV1(
    filters?: ApiRequestFilters
  ): Observable<PaginatedModel<IInboundListItemModel>> {
    const url = `${this.baseApiUrl}/v1/Inbound`;

    const params = this.filterParameterService.toHttpParams({
      ...filters,
      sites: this.getGlobalSitesFilter(),
    });

    return this.http.get<PaginatedModel<IInboundListItemModel>>(url, {
      params,
    });
  }

  getInboundDetailsV1(id: number): Observable<IInboundDetailsModel> {
    const url = `${this.baseApiUrl}/V1/Inbound/${id}`;
    return this.http.get<IInboundDetailsModel>(url, {});
  }

  getInboundDetailsByLabelCodeV1(
    labelCode: string
  ): Observable<IInboundDetailsModel> {
    const url = `${this.baseApiUrl}/V1/Inbound/${labelCode}`;
    return this.http.get<IInboundDetailsModel>(url);
  }

  getInboundDeliveryInstructionsV1(
    id: number
  ): Observable<IInboundDeliveryInstructionsResponseModel> {
    const url = `${this.baseApiUrl}/V1/Inbound/${id}/DeliveryInstructions`;
    return this.http.get<IInboundDeliveryInstructionsResponseModel>(url, {});
  }

  createInboundV1(
    model: ICreateInboundModel
  ): Observable<ObjectCreatedResponseModel> {
    const url = `${this.baseApiUrl}/v1/Inbound`;
    return this.http.post<ObjectCreatedResponseModel>(url, model);
  }

  uploadInboundPhotosV1(files: File[]): Observable<IInboundPhoto[]> {
    const url = `${this.baseApiUrl}/v1/Photo/Inbound`;
    const formData = new FormData();
    Array.from(files).map((file, index) => {
      return formData.append("file" + index, file, file.name);
    });
    return this.http.post<IInboundPhoto[]>(url, formData);
  }

  updateInboundNotesV1(
    id: number,
    model: IInboundNotesUpdateModel
  ): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Inbound/${id}/Notes`;
    return this.http.put<void>(url, model);
  }

  approveAndCompleteInboundV1(inboundId: number): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Inbound/${inboundId}/Approve`;
    return this.http.put<void>(url, {});
  }

  getInboundStatusBadgeClassName(status: string): string {
    const classBase = "chip chip-";
    const stuffix = "-strong";
    if (status) {
      switch (this.removeSpaces(status.toLowerCase())) {
        case "new": {
          return classBase + "info" + stuffix;
        }
        case "complete": {
          return classBase + "success" + stuffix;
        }
        default: {
          return classBase + "accent" + stuffix;
        }
      }
    }
    return "";
  }

  getPrintLabelUrl(inboundId: number): string {
    return `${environment.logisticsUrl}/v1/Inbound/${inboundId}/labels`;
  }

  private removeSpaces(value: string): string {
    return value.replace(/\s/g, "");
  }

  getInboundProcessingStatusesV1(
    filterText: string
  ): Observable<KeyValuePairModel<number>[]> {
    const url = `${this.baseApiUrl}/v1/Inbound/processingstatuses`;
    const params = new HttpParams().append("filterText", filterText);
    return this.http.get<KeyValuePairModel<number>[]>(url, { params });
  }

  getInboundFiltersV1(): Observable<IInboundGetFilterModel> {
    const url = `${this.baseApiUrl}/v1/Inbound/filters`;
    return this.http.get<IInboundGetFilterModel>(url);
  }

  getGlobalSitesFilter = (): Object => {
    const siteIds = this.globalFilterService.getCachedSites();
    if (siteIds.length === 1 && siteIds.includes(0)) {
      return null;
    }

    return siteIds;
  };
}
